"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.resolveTooltip = exports.maybeTooltip = exports.Tooltip = void 0;
const jsx_runtime_1 = require("@emotion/react/jsx-runtime");
const react_1 = require("react");
const react_aria_1 = require("react-aria");
const react_dom_1 = require("react-dom");
const react_popper_1 = require("react-popper");
const react_stately_1 = require("react-stately");
const Css_1 = require("../Css");
const utils_1 = require("../utils");
function Tooltip(props) {
    const { placement, children, title, disabled, delay = 0 } = props;
    const state = (0, react_stately_1.useTooltipTriggerState)({ delay, isDisabled: disabled });
    const triggerRef = (0, react_1.useRef)(null);
    const { triggerProps, tooltipProps: _tooltipProps } = (0, react_aria_1.useTooltipTrigger)({ isDisabled: disabled }, state, triggerRef);
    const { tooltipProps } = (0, react_aria_1.useTooltip)(_tooltipProps, state);
    const tid = (0, utils_1.useTestIds)(props, "tooltip");
    return ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsx)("span", { ref: triggerRef, ...triggerProps, ...(!state.isOpen && typeof title === "string" ? { title } : {}), ...tid, 
                // Add display contents to prevent the tooltip wrapping element from short-circuiting inherited styles (i.e. flex item positioning)
                // Once the element is `:active`, allow pointer events (i.e. click events) to pass through to the children.
                css: Css_1.Css.display("contents").addIn(":active", Css_1.Css.add("pointerEvents", "none").$).$, 
                // Adding `draggable` as a hack to allow focus to continue through this element and into its children.
                // This is due to some code in React-Aria that prevents default due ot mobile browser inconsistencies,
                // and the only way they don't prevent default is if the element is draggable.
                // See https://github.com/adobe/react-spectrum/discussions/3058 for discussion related to this issue.
                draggable: true, onDragStart: (e) => e.preventDefault(), children: children }), state.isOpen && ((0, jsx_runtime_1.jsx)(Popper, { ...(0, react_aria_1.mergeProps)(_tooltipProps, tooltipProps), triggerRef: triggerRef, content: title, placement: placement }))] }));
}
exports.Tooltip = Tooltip;
function Popper({ triggerRef, content, placement = "auto" }) {
    var _a;
    const popperRef = (0, react_1.useRef)(null);
    const [arrowRef, setArrowRef] = (0, react_1.useState)(null);
    // Since we use `display: contents;` on the `triggerRef`, then the element.offsetTop/Left/etc all equal `0`. This would make
    // the tooltip show in the top left of the document. So instead, we target either the first child, if available, or the parent element as the tooltip target.
    // It is possible there are no children if the element only has text content, which is the reasoning for the parentElement fallback.
    const targetElement = triggerRef.current ? (_a = triggerRef.current.children[0]) !== null && _a !== void 0 ? _a : triggerRef.current.parentElement : null;
    const { styles, attributes } = (0, react_popper_1.usePopper)(targetElement, popperRef.current, {
        modifiers: [
            { name: "arrow", options: { element: arrowRef } },
            { name: "offset", options: { offset: [0, 5] } },
        ],
        placement,
    });
    return (0, react_dom_1.createPortal)((0, jsx_runtime_1.jsxs)("div", { ref: popperRef, style: styles.popper, ...attributes.popper, css: Css_1.Css.maxw("320px").bgGray900.white.px1.py("4px").br4.xs.add("zIndex", 999999).$, children: [(0, jsx_runtime_1.jsx)("div", { ref: setArrowRef, style: { ...styles.arrow }, id: "arrow" }), content] }), document.body);
}
// Helper function to conditionally wrap component with Tooltip if necessary.
function maybeTooltip(props) {
    return props.title ? (0, jsx_runtime_1.jsx)(Tooltip, { ...props }) : (0, jsx_runtime_1.jsx)(jsx_runtime_1.Fragment, { children: props.children });
}
exports.maybeTooltip = maybeTooltip;
// Helper function for resolving showing the Tooltip text via a 'disabled' prop, or the 'tooltip' prop.
function resolveTooltip(disabled, tooltip, readOnly) {
    // If `disabled` is a ReactNode, then return that. Otherwise, return `tooltip`
    return typeof disabled !== "boolean" && disabled
        ? disabled
        : typeof readOnly !== "boolean" && readOnly
            ? readOnly
            : tooltip !== null && tooltip !== void 0 ? tooltip : undefined;
}
exports.resolveTooltip = resolveTooltip;
